import React from 'react';
import { Plus } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { formatINR } from '@/lib/money';
import { PlaceholderImage } from './PlaceholderImage';
import { TID } from '@/constants/testIds';

const renderProductMedia = (product, isKids) => {
  if (product.placeholder) {
    return (
      <PlaceholderImage
        label={product.name}
        bg={product.placeholderColor}
        ink={product.placeholderInk}
      />
    );
  }
  if (product.imageCrop) {
    return (
      <div
        className="w-full h-full"
        style={{
          backgroundImage: `url(${product.image})`,
          backgroundSize: product.imageCrop.size,
          backgroundPosition: product.imageCrop.pos,
          backgroundRepeat: 'no-repeat',
        }}
        role="img"
        aria-label={product.name}
      />
    );
  }
  return (
    <img
      src={product.image}
      alt={product.name}
      loading="lazy"
      style={isKids ? { objectFit: 'contain', padding: '0.5rem' } : undefined}
    />
  );
};

export const ProductCard = ({ product, accent = 'blue' }) => {
  const { addItem } = useCart();
  const accentBg = accent === 'yellow' ? 'var(--cw-yellow)' : 'var(--cw-blue)';
  const isKids = product.id === 'soap-kids';
  const isPlaceholder = product.placeholder;

  const cardStyle = isKids
    ? { backgroundColor: '#FFFDF3', borderColor: '#F5E9B7' }
    : undefined;

  const imgWrapBg = isKids
    ? '#FFF7DC'
    : isPlaceholder
      ? product.placeholderColor || '#EFE7DC'
      : '#fff';

  return (
    <article
      className="cw-card p-5 flex flex-col gap-4"
      data-testid={TID.productCard(product.id)}
      style={cardStyle}
    >
      <div className="cw-img-wrap" style={{ backgroundColor: imgWrapBg }}>
        {renderProductMedia(product, isKids)}
        {product.badge && (
          <span
            className="cw-badge absolute top-3 left-3 shadow-sm"
            style={isKids ? { backgroundColor: '#FFD75E', color: '#3A2A00' } : undefined}
          >
            {product.badge}
          </span>
        )}
        {isKids && (
          <span className="absolute bottom-3 right-3 text-[10px] uppercase tracking-[0.18em] font-semibold bg-white/85 backdrop-blur-sm rounded-full px-3 py-1 text-[var(--cw-text)] border border-white/70">
            Made for kids
          </span>
        )}
      </div>

      <div className="flex-1 flex flex-col gap-1">
        <h3 className="font-heading text-lg font-medium leading-tight">{product.name}</h3>
        <p className="text-sm text-[var(--cw-muted)] leading-snug">{product.tagline}</p>
      </div>

      <div className="flex items-center justify-between pt-2">
        <div className="font-heading text-xl font-medium">{formatINR(product.price)}</div>
        <button
          type="button"
          onClick={() => addItem(product)}
          className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
          style={{ backgroundColor: accentBg }}
          aria-label={`Add ${product.name} to cart`}
          data-testid={TID.addToCartBtn(product.id)}
        >
          <Plus className="w-4 h-4 text-[var(--cw-text)]" />
        </button>
      </div>
    </article>
  );
};
